import { FC, useCallback, useEffect, useMemo } from 'react';
import { Info } from 'lucide-react';
import { useAppDispatch } from 'app/hooks';
import { setAnswer } from 'features/subscription-process/profiling-process/slices/genericProfilingSlice';
import { PossibleAnswer, Question } from 'features/subscription-process/profiling-process/types/interfaces';
import { isLabelAnswer, isIconAnswer } from 'features/subscription-process/profiling-process/types/possibleAnswer';
import { QuickGroupSurveyProps } from './quickGroupSurvey.types';
import { QuickGroupSurveyQuestion } from './QuickGroupSurveyQuestion';

const DEFAULT_DROPDOWN_THRESHOLD = 4;

// Helper pour vérifier si une question doit être visible selon dependsOn
const isQuestionVisible = (
  question: Question,
  answers: Record<string, PossibleAnswer[]>
): boolean => {
  if (!question.dependsOn) return true;

  const { questionKey, values } = question.dependsOn;
  const dependentAnswers = answers[questionKey];

  if (!dependentAnswers || dependentAnswers.length === 0) return false;

  return dependentAnswers.some(answer => {
    if (isLabelAnswer(answer) || isIconAnswer(answer)) {
      return values.includes(answer.key);
    }
    if (typeof answer === 'string' || typeof answer === 'number') {
      return values.includes(answer);
    }
    return false;
  });
};

// Helper pour obtenir le message d'éligibilité actuel d'une question
const getEligibilityWarning = (
  questionKey: string,
  currentAnswers: PossibleAnswer[] | undefined,
  eligibilityMessages: Record<string, Record<string, string>> | undefined
): string | undefined => {
  if (!eligibilityMessages || !currentAnswers || currentAnswers.length === 0) {
    return undefined;
  }

  const questionMessages = eligibilityMessages[questionKey];
  if (!questionMessages) return undefined;

  // Chercher un message pour les réponses actuelles
  for (const answer of currentAnswers) {
    let answerKey: string;
    if (typeof answer === 'object' && answer !== null && 'key' in answer) {
      answerKey = String((answer as any).key);
    } else {
      answerKey = String(answer);
    }

    if (questionMessages[answerKey]) {
      return questionMessages[answerKey];
    }
  }

  return undefined;
};

export const QuickGroupSurvey: FC<QuickGroupSurveyProps> = ({
  groups,
  existingAnswers,
  onChange,
  dropdownThreshold = DEFAULT_DROPDOWN_THRESHOLD,
  title,
  description,
  showEligibilityWarnings = false,
  eligibilityMessages,
}) => {
  const dispatch = useAppDispatch();

  // Notifier le parent quand les réponses changent
  useEffect(() => {
    if (existingAnswers && onChange) {
      onChange(existingAnswers);
    }
  }, [existingAnswers, onChange]);

  // Fusionner toutes les questions de tous les groupes
  const allQuestions = useMemo(() => {
    return groups.flatMap(g => g.questions);
  }, [groups]);

  // Filtrer les questions visibles
  const visibleQuestions = useMemo(() => {
    return allQuestions.filter(question =>
      isQuestionVisible(question, existingAnswers || {})
    );
  }, [allQuestions, existingAnswers]);

  const isNoneAnswer = useCallback((answer: PossibleAnswer): boolean => {
    if (typeof answer === 'string') return answer.toLowerCase() === 'none';
    if (typeof answer === 'object' && answer !== null && 'key' in answer) {
      return String((answer as any).key).toLowerCase() === 'none';
    }
    return false;
  }, []);

  const onAnswer = useCallback((questionKey: string, answer: PossibleAnswer) => {
    const question = allQuestions.find(q => q.key === questionKey);

    // Gérer le choix multiple
    const isMultipleChoice = question?.isMultipleChoice ?? false;

    if (isMultipleChoice) {
      const currentAnswers = existingAnswers?.[questionKey] || [];
      const isNone = isNoneAnswer(answer);

      const isAlreadySelected = currentAnswers.some(ca => {
        if (typeof ca === 'object' && ca !== null && 'key' in ca &&
            typeof answer === 'object' && answer !== null && 'key' in answer) {
          return (ca as any).key === (answer as any).key;
        }
        return ca === answer;
      });

      let newAnswers: PossibleAnswer[];

      if (isNone) {
        // Si on sélectionne "none"/"aucune", on remplace tout par cette seule réponse
        // Si déjà sélectionné, on le retire
        newAnswers = isAlreadySelected ? [] : [answer];
      } else if (isAlreadySelected) {
        // Retirer la réponse
        newAnswers = currentAnswers.filter(ca => {
          if (typeof ca === 'object' && ca !== null && 'key' in ca &&
              typeof answer === 'object' && answer !== null && 'key' in answer) {
            return (ca as any).key !== (answer as any).key;
          }
          return ca !== answer;
        });
      } else {
        // Ajouter la réponse en retirant les éventuelles réponses "none"
        const withoutNone = currentAnswers.filter(ca => !isNoneAnswer(ca));
        newAnswers = [...withoutNone, answer];
      }

      dispatch(setAnswer({ questionKey, value: newAnswers }));
    } else {
      // Choix simple: remplacer la réponse
      dispatch(setAnswer({ questionKey, value: [answer] }));
    }
  }, [allQuestions, existingAnswers, dispatch, isNoneAnswer]);

  const handleAnswer = useCallback((questionKey: string) => (answer: PossibleAnswer) => {
    onAnswer(questionKey, answer);
  }, [onAnswer]);

  if (visibleQuestions.length === 0) return null;

  const hasHeader = title || description;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      {hasHeader && (
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <Info className="w-4 h-4 text-primary flex-shrink-0" />
          <div>
            {title && (
              <h3 className="text-sm font-semibold text-slate-700">
                {title}
              </h3>
            )}
            {description && (
              <p className="text-xs text-slate-500 mt-0.5">
                {description}
              </p>
            )}
          </div>
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {visibleQuestions.map((question) => {
          const warningMessage = showEligibilityWarnings
            ? getEligibilityWarning(question.key, existingAnswers?.[question.key], eligibilityMessages)
            : undefined;

          return (
            <QuickGroupSurveyQuestion
              key={question.key}
              question={question}
              currentAnswers={existingAnswers?.[question.key]}
              onAnswer={handleAnswer(question.key)}
              dropdownThreshold={dropdownThreshold}
              warningMessage={warningMessage}
            />
          );
        })}
      </div>
    </div>
  );
};
